import React, { useMemo } from 'react';
import { cn } from '@/shared/lib/utils';

interface CircularProgressProps {
  /**
   * Current progress value (0-100)
   */
  progress: number;
  /**
   * Goal start date (ISO string)
   */
  startDate?: string;
  /**
   * Goal due date (ISO string)
   */
  dueDate?: string;
  /**
   * Size of the circle in pixels
   */
  size?: number;
  /**
   * Stroke width in pixels
   */
  strokeWidth?: number;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Whether to show the percentage text inside the circle
   */
  showPercentage?: boolean;
  /**
   * Whether this is a minimum KPI (lower is better)
   * For minimum KPIs, colors are reversed: low progress = red, high progress = green
   */
  isMinimum?: boolean;
}

type ProgressStatus = 'success' | 'warning' | 'danger';

/**
 * Calculate the progress status based on time elapsed vs progress made
 *
 * Logic for Maximum KPIs:
 * - Green (success): Progress >= time elapsed - 10% (on track or ahead)
 * - Orange (warning): Progress is 10-20% behind time elapsed
 * - Red (danger): Progress is >20% behind time elapsed
 *
 * Logic for Minimum KPIs (reversed):
 * - Red (danger): Progress is ahead of time (accumulating problems too fast)
 *   Example: 20% time passed, but already at 70% of complaint limit
 * - Orange (warning): Progress roughly matches time elapsed (risky)
 * - Green (success): Progress is behind time (staying under limit well)
 *   Example: 80% time passed, but only at 30% of complaint limit
 */
const calculateProgressStatus = (
  progress: number,
  startDate?: string,
  dueDate?: string,
  isMinimum = false
): ProgressStatus => {
  // If no dates provided, use progress only
  if (!startDate || !dueDate) {
    if (isMinimum) {
      // For minimum KPIs: high progress = good (few complaints), low progress = bad (many complaints)
      if (progress >= 70) return 'success';
      if (progress >= 40) return 'warning';
      return 'danger';
    } else {
      // For maximum KPIs: high progress = good, low progress = bad
      if (progress >= 70) return 'success';
      if (progress >= 40) return 'warning';
      return 'danger';
    }
  }

  const now = new Date().getTime();
  const start = new Date(startDate).getTime();
  const due = new Date(dueDate).getTime();

  // Calculate time elapsed as a percentage
  const totalTime = due - start;
  const timeElapsed = now - start;
  const timeElapsedPercentage = Math.min(
    Math.max((timeElapsed / totalTime) * 100, 0),
    100
  );

  // Calculate the difference between progress and time elapsed
  const progressDelta = progress - timeElapsedPercentage;

  if (isMinimum) {
    // For minimum KPIs: progress represents remaining capacity (higher = better)
    // The progress value is already inverted: (quantity - currentProgress) / quantity
    // So 5/10 complaints = 50% progress (50% capacity remaining)
    // And 7/10 complaints = 30% progress (30% capacity remaining)
    //
    // Example: 10 day goal to keep complaints under 10
    // - Day 2 (20% time): 7 complaints (30% progress/remaining) → progressDelta = 30-20 = +10
    //   This is BAD! Low remaining capacity despite early time → should be RED
    // - Day 8 (80% time): 3 complaints (70% progress/remaining) → progressDelta = 70-80 = -10
    //   This is GOOD! High remaining capacity despite late time → should be GREEN
    //
    // Since progress represents remaining capacity (higher is better), we use similar logic to maximum KPIs
    if (progressDelta >= -10) {
      // Progress on track or ahead of time = good remaining capacity = SUCCESS
      return 'success';
    } else if (progressDelta >= -20) {
      // Progress slightly behind time = getting risky = WARNING
      return 'warning';
    } else {
      // Progress way behind time = low remaining capacity = DANGER
      return 'danger';
    }
  } else {
    // For maximum KPIs: normal logic
    if (progressDelta >= -10) {
      return 'success';
    } else if (progressDelta >= -20) {
      return 'warning';
    } else {
      return 'danger';
    }
  }
};

/**
 * Get color classes based on progress status
 */
const getColorClasses = (status: ProgressStatus) => {
  switch (status) {
    case 'success':
      return {
        stroke: 'stroke-emerald-500',
        text: 'text-emerald-600',
        bg: 'stroke-emerald-100',
      };
    case 'warning':
      return {
        stroke: 'stroke-orange-500',
        text: 'text-orange-600',
        bg: 'stroke-orange-100',
      };
    case 'danger':
      return {
        stroke: 'stroke-red-500',
        text: 'text-red-600',
        bg: 'stroke-red-100',
      };
  }
};

export const CircularProgress: React.FC<CircularProgressProps> = ({
  progress,
  startDate,
  dueDate,
  size = 64,
  strokeWidth = 6,
  className,
  showPercentage = true,
  isMinimum = false,
}) => {
  const clampedProgress = Math.min(Math.max(progress, 0), 100);

  const status = useMemo(
    () =>
      calculateProgressStatus(clampedProgress, startDate, dueDate, isMinimum),
    [clampedProgress, startDate, dueDate, isMinimum]
  );

  const colorClasses = getColorClasses(status);

  // Calculate SVG circle properties
  const center = size / 2;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset =
    circumference - (clampedProgress / 100) * circumference;

  return (
    <div
      className={cn(
        'relative inline-flex items-center justify-center',
        className
      )}
    >
      <svg width={size} height={size} className="transform -rotate-90">
        {/* Background circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          strokeWidth={strokeWidth}
          className={cn('fill-none', colorClasses.bg)}
          opacity={0.2}
        />

        {/* Progress circle */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          strokeWidth={strokeWidth}
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          strokeLinecap="round"
          className={cn(
            'fill-none transition-all duration-500 ease-out',
            colorClasses.stroke
          )}
        />
      </svg>

      {/* Percentage text */}
      {showPercentage && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className={cn('text-xs font-semibold', colorClasses.text)}>
            {Math.round(clampedProgress)}%
          </span>
        </div>
      )}
    </div>
  );
};
